// Common email domain typo detector — catches the majority of no-show
// causing typos ("gmial.com", "hotmial.com", "bexpca.com") without needing
// a network call. Levenshtein distance ≤ 2 against a curated safelist.
// Returns null when the domain is fine or the suggestion is uncertain.
const COMMON_DOMAINS = [
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in',
  'hotmail.com', 'hotmail.co.uk',
  'outlook.com', 'live.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'msn.com',
  'protonmail.com', 'proton.me',
  'comcast.net', 'verizon.net', 'sbcglobal.net', 'att.net',
  'countbucks.com', 'bexcpa.com',
];

// Common typos that are within edit-distance 3 of the correct domain but
// which the loose distance-only check would still flag ambiguously. This
// map is a fast-path for high-confidence corrections.
const EXPLICIT_MAP = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmailcom': 'gmail.com',
  'yhaoo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahho.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmial.co': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'oultook.com': 'outlook.com',
  'iclou.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'bexpca.com': 'bexcpa.com',
  'contbucks.com': 'countbucks.com',
  'countbuks.com': 'countbucks.com',
};

function levenshtein(a, b) {
  if (a === b) return 0;
  const al = a.length, bl = b.length;
  if (!al) return bl;
  if (!bl) return al;
  const dp = Array.from({ length: bl + 1 }, (_, i) => i);
  for (let i = 1; i <= al; i++) {
    let prev = dp[0]; dp[0] = i;
    for (let j = 1; j <= bl; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1]
        ? prev
        : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[bl];
}

export function suggestEmailDomain(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) return null;
  const [local, domainRaw] = email.split('@');
  if (!local || !domainRaw) return null;
  const domain = domainRaw.toLowerCase();

  // Domain already fine → no suggestion.
  if (COMMON_DOMAINS.includes(domain)) return null;

  // Explicit typo table — highest confidence, always suggest.
  if (EXPLICIT_MAP[domain]) {
    return `${local}@${EXPLICIT_MAP[domain]}`;
  }

  // Fuzzy match — suggest the closest common domain when edit distance
  // is ≤ 2 (empirically catches typos while avoiding false positives on
  // legitimate custom domains).
  let best = null;
  let bestDist = 3;
  for (const cand of COMMON_DOMAINS) {
    const d = levenshtein(domain, cand);
    if (d > 0 && d < bestDist) {
      best = cand;
      bestDist = d;
    }
  }
  if (best && bestDist <= 2) return `${local}@${best}`;
  return null;
}
